import { useEffect, useState } from "react";
import { X, ChevronRight, ChevronLeft, MapPin, Plus, Search, Satellite, MousePointerClick, Info, ShieldCheck, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";

// Bump this when you add material tour changes so returning users see it again.
export const TOUR_VERSION = "v4";
const TOUR_KEY = "kolayarsivim_tour_seen";

const STEPS = [
  {
    icon: MapPin,
    title: "Kolay Arşivim'e Hoş Geldiniz",
    body: "Ekspertize gittiğiniz her taşınmazı harita üzerinde işaretleyip düzenli tutabilirsiniz. Kısa bir tur ile temel özellikleri gösterelim — 30 saniye sürer.",
    target: null,
  },
  {
    icon: ShieldCheck,
    title: "Verileriniz Size Özel",
    body: "Harita üzerinde girdiğiniz veriler sizin kişisel çalışma notlarınızdır. Sadece harita üzerinde kolay erişim imkânı sağlıyoruz. Girdiğiniz veriler başka kullanıcılar tarafından görülemez.",
    target: null,
  },
  {
    icon: Plus,
    title: "Yeni Ekspertiz Ekle",
    body: "Sol üstteki 'Yeni Ekspertiz Ekle' butonuyla yeni bir kayıt açarsınız. Tarih, taşınmaz türü, konum ve değer bilgilerini girip haritada işaretleyebilirsiniz.",
    target: '[data-testid="add-expertise-btn"]',
    placement: "right",
  },
  {
    icon: Search,
    title: "Hızlı Arama",
    body: "Arama kutusuna mahalle adı, ada/parsel veya notlarınızı yazarak kayıtlarınızı anında filtreleyebilirsiniz. 'Yakınımda Ara' ile bulunduğunuz noktaya yakın kayıtları görürsünüz.",
    target: '[data-testid="search-input"], input[placeholder*="Mahalle"]',
    placement: "right",
  },
  {
    icon: Satellite,
    title: "Harita / Uydu Görünümü",
    body: "Sağ üstten harita ile uydu (Google) görünümü arasında geçiş yapabilirsiniz. Uydu görünümü bina cephelerini incelerken çok işe yarar.",
    target: '[data-testid="layer-satellite-btn"]',
    placement: "left",
  },
  {
    icon: MousePointerClick,
    title: "Harita Üzerinde Sağ Tık",
    body: "Haritada herhangi bir yere SAĞ TIKLARSANIZ 'Ekspertiz Ekle' menüsü açılır — o konum otomatik işaretli olarak yeni kayıt formu açılır. Sahada en hızlı yol budur.",
    target: '[data-testid="expertise-map"]',
    placement: "center",
  },
  {
    icon: Info,
    title: "Kayıt Detayları",
    body: "Haritadaki pin'e veya sol taraftaki listede bir kayda tıklayınca sol panel yarı ekrana genişler ve tüm detaylar açılır. Buradan Güncelle butonuyla düzenleyebilir, çoğaltabilir veya silebilirsiniz.",
    target: null,
  },
  {
    icon: FileText,
    title: "Açıklamalar ve PDF Rapor",
    body: "Kayıt formundaki 'Açıklamalar' alanına özet rapor metninizi yazabilirsiniz — bu metin PDF rapor çıktısında aynen yer alır. İpucu: Tamamen BÜYÜK HARFLERLE yazdığınız satırlar raporda koyu renk başlık olarak gösterilir. Detay panelindeki 'Rapor' butonuyla uydu görüntülü PDF çıktısı alabilirsiniz.",
    target: null,
  },
];

export default function GuidedTour() {
  const [open, setOpen] = useState(false);
  const [idx, setIdx] = useState(0);
  const [pos, setPos] = useState(null);

  useEffect(() => {
    const seen = localStorage.getItem(TOUR_KEY);
    if (seen !== TOUR_VERSION) {
      // Delay so app has rendered
      const t = setTimeout(() => {
        // Auto-dismiss cookie banner during tour so it doesn't overlap
        // the tour cards on small viewports. User has already accepted the
        // 4 legal consents at registration; the cookie banner is a redundant
        // reminder in this app.
        try {
          if (!localStorage.getItem("cookie_consent_v1")) {
            localStorage.setItem(
              "cookie_consent_v1",
              JSON.stringify({ v: 1, at: Date.now(), via: "tour" })
            );
            // Notify CookieConsent (if listening) to hide itself
            window.dispatchEvent(new CustomEvent("cookie-consent-updated"));
          }
        } catch (_) {}
        setOpen(true);
      }, 800);
      return () => clearTimeout(t);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    const step = STEPS[idx];
    if (!step?.target) {
      setPos(null);
      return;
    }

    const updatePos = () => {
      const el = document.querySelector(step.target);
      if (!el) {
        setPos(null);
        return;
      }
      const r = el.getBoundingClientRect();
      setPos({
        top: r.top,
        left: r.left,
        width: r.width,
        height: r.height,
        placement: step.placement || "bottom",
      });
    };

    updatePos();
    // Reposition on resize/scroll so cards always stay within viewport
    window.addEventListener("resize", updatePos);
    window.addEventListener("scroll", updatePos, true);
    return () => {
      window.removeEventListener("resize", updatePos);
      window.removeEventListener("scroll", updatePos, true);
    };
  }, [open, idx]);

  const close = () => {
    localStorage.setItem(TOUR_KEY, TOUR_VERSION);
    setOpen(false);
    setIdx(0);
  };
  const next = () => (idx < STEPS.length - 1 ? setIdx(idx + 1) : close());
  const prev = () => idx > 0 && setIdx(idx - 1);

  if (!open) return null;
  const step = STEPS[idx];
  const Icon = step.icon;
  const isCentered = !pos || step.placement === "center";

  // Compute tooltip position with viewport clamping so cards never spill off screen.
  const W = 360; // tooltip width (matches Tailwind w-[360px])
  const H = 220; // approximate tooltip height
  const M = 12; // safety margin from viewport edge
  const pad = 14; // gap between target ring and tooltip
  const vw = typeof window !== "undefined" ? window.innerWidth : 1200;
  const vh = typeof window !== "undefined" ? window.innerHeight : 800;

  let tooltipStyle;
  if (isCentered) {
    tooltipStyle = {
      position: "fixed",
      top: Math.max(M, (vh - H) / 2),
      left: Math.max(M, (vw - W) / 2),
    };
  } else {
    let preferred = step.placement || "bottom";
    // Auto-flip when preferred placement would overflow.
    if (preferred === "right" && pos.left + pos.width + pad + W > vw - M) preferred = "left";
    if (preferred === "left" && pos.left - pad - W < M) preferred = "right";
    if (preferred === "bottom" && pos.top + pos.height + pad + H > vh - M) preferred = "top";

    let left, top;
    if (preferred === "right") {
      left = pos.left + pos.width + pad;
      top = pos.top + pos.height / 2 - H / 2;
    } else if (preferred === "left") {
      left = pos.left - W - pad;
      top = pos.top + pos.height / 2 - H / 2;
    } else if (preferred === "top") {
      left = pos.left;
      top = pos.top - H - pad;
    } else {
      // bottom
      left = pos.left;
      top = pos.top + pos.height + pad;
    }

    // Final viewport clamp
    left = Math.max(M, Math.min(vw - W - M, left));
    top = Math.max(M, Math.min(vh - H - M, top));

    tooltipStyle = { position: "fixed", top, left };
  }

  return (
    <>
      {/* Dim overlay with cutout for target */}
      <div
        className="fixed inset-0 z-[3000] bg-black/60 backdrop-blur-[1px] transition-opacity"
        onClick={close}
        data-testid="tour-overlay"
      />
      {/* Highlight ring around target */}
      {pos && step.placement !== "center" && (
        <div
          className="fixed z-[3001] pointer-events-none border-2 border-emerald-400 rounded-md shadow-[0_0_0_9999px_rgba(0,0,0,0.6)] transition-all"
          style={{
            top: pos.top - 6,
            left: pos.left - 6,
            width: pos.width + 12,
            height: pos.height + 12,
          }}
        />
      )}
      {/* Tooltip card */}
      <div
        className="z-[3002] w-[360px] max-w-[92vw] bg-white rounded-md shadow-2xl border border-zinc-200 overflow-hidden"
        style={tooltipStyle}
        data-testid="tour-card"
      >
        <div className="flex items-start gap-3 p-4 pb-3">
          <div className="h-9 w-9 rounded-md bg-emerald-50 border border-emerald-200 grid place-items-center text-emerald-600 shrink-0">
            <Icon className="h-4 w-4" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2">
              <h3 className="text-sm font-bold text-zinc-900 tracking-tight">{step.title}</h3>
              <button
                type="button"
                onClick={close}
                className="text-zinc-400 hover:text-zinc-700 -mr-1"
                aria-label="Kapat"
                data-testid="tour-close-btn"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <p className="mt-1.5 text-xs text-zinc-600 leading-relaxed">{step.body}</p>
          </div>
        </div>

        <div className="flex items-center justify-between border-t border-zinc-100 px-4 py-2.5 bg-zinc-50">
          <div className="flex items-center gap-1">
            {STEPS.map((_, i) => (
              <span
                key={i}
                className={`h-1.5 rounded-full transition-all ${
                  i === idx ? "w-6 bg-emerald-500" : "w-1.5 bg-zinc-300"
                }`}
              />
            ))}
          </div>
          <div className="flex items-center gap-2">
            {idx > 0 && (
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={prev}
                className="h-7 text-xs"
                data-testid="tour-prev-btn"
              >
                <ChevronLeft className="h-3.5 w-3.5 mr-0.5" />
                Geri
              </Button>
            )}
            <Button
              type="button"
              size="sm"
              onClick={next}
              className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="tour-next-btn"
            >
              {idx === STEPS.length - 1 ? "Bitir" : "İleri"}
              {idx < STEPS.length - 1 && <ChevronRight className="h-3.5 w-3.5 ml-0.5" />}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}
